import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Card,
  SkeletonBodyText,
  Page,
  ChoiceList,
  Badge,
  Filters,
  TextStyle,
} from "@shopify/polaris";
import { ShopContext } from "../../App";
import currencySymbol from "currency-symbol-map";
import { DateTime } from "luxon";
import axios from "../../axios";
import { useHistory } from "react-router-dom";
import Br from "../../components/Br";
import { getMessageType } from "../../hooks/getUrlParams";
import Truncate from "../../components/Truncate";

function MessageHistory() {
  const config = useContext(ShopContext);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [messages, setMessages] = useState([]);
  const [pageDetails, setPageDetails] = useState({
    totalPages: 0,
    previousPage: false,
    nextPage: false,
    totalItems: 0,
  });

  const router = useHistory();

  /**
   * get the skeleton content for page data table
   * @returns {[]}
   */
  const getTableSkeletonContent = () => {
    let skeleton = [];
    for (let i = 0; i < 4; i++) {
      skeleton.push([
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
      ]);
    }
    return skeleton;
  };
  let skeleton = getTableSkeletonContent();
  /**
   * Column content types of data table
   * @returns {string[]}
   */
  const columnContentTypes = () => {
    return ["text", "text", "text", "text", "text", "text"];
  };
  /**
   * Data table headings
   * @returns {string[]}
   */
  const tableHeadings = () => {
    return ["Customer", "Status", "Message", "Date", "Type", "Amount"];
  };

  /**
   * process the response to generate the table columns data
   * @param messages
   * @param shop
   * @returns {[]}
   */
  const _processResponseMessages = (messages, shop) => {
    let processedMessages = [];
    if (messages.length > 0) {
      messages.forEach((message) => {
        let messageColumnContent = [];
        messageColumnContent.push(
          <>
            <div>{message.firstname + " " + message.lastname}</div>
            <Br height={5} />
            <div>{message.phone_number}</div>
            <Br height={5} />
            <div>{message.customer_email}</div>
          </>
        );

        if (message.status === "queued") {
          messageColumnContent.push(<Badge status="warning"> Queued </Badge>);
        } else if (message.status === "read") {
          messageColumnContent.push(<Badge status="success"> Read </Badge>);
        } else if (message.status === "failed") {
          messageColumnContent.push(<Badge status="critical"> Failed </Badge>);
        } else {
          messageColumnContent.push(<Badge status="attention"> Queued </Badge>);
        }

        messageColumnContent.push(
          <>
            <p className="message_history_content">{message.message}</p>
          </>
        );
        messageColumnContent.push(
          <>
            <div>
              <b>Created: </b>
              {DateTime.fromMillis(parseInt(message.created_at)).toLocaleString(
                DateTime.DATETIME_SHORT
              )}
            </div>
            <Br height={10} />
            {message.is_delivered !== null && (
              <>
                <div>
                  <b>Delivered at: </b>
                  {DateTime.fromMillis(
                    parseInt(message.is_delivered)
                  ).toLocaleString(DateTime.DATETIME_SHORT)}
                </div>
              </>
            )}
            {message.is_opened !== null && (
              <>
                <Br height={10} />
                <div>
                  <b>Read at: </b>
                  {DateTime.fromMillis(
                    parseInt(message.is_opened)
                  ).toLocaleString(DateTime.DATETIME_SHORT)}
                </div>
              </>
            )}
          </>
        );

        if (message.type === "pre_order") {
          messageColumnContent.push("Abandoned Cart");
        } else if (message.type === "post_order_payment") {
          messageColumnContent.push("Order confirmation");
        } else if (message.type === "post_order_delivery") {
          messageColumnContent.push("Order delivery");
        } else {
          messageColumnContent.push(message.type);
        }

        if (message.price !== null) {
          messageColumnContent.push(
            <>
              {currencySymbol(message.price_unit)}
              {parseFloat(message.price).toFixed(2)}
            </>
          );
        } else {
          messageColumnContent.push(<>-</>);
        }

        processedMessages.push(messageColumnContent);
      });
    }
    return processedMessages;
  };
  /**
   * Load the checkouts from API
   * @type {function(): void}
   */
  const loadOrders = (message_type = null) => {
    setLoading(true);
    let url = `/message-history?page=${page}`;
    if (
      message_type !== undefined &&
      message_type !== null &&
      Array.isArray(message_type)
    ) {
      if (message_type.length > 0) {
        let message_type_string = message_type.toString();
        url = `/message-history?type=${message_type_string}`;
      }
    }
    axios
      .get(url, {
        headers: {
          shopOrigin: config.shopOrigin,
          xShopHost: config.host,
        },
      })
      .then((response) => {
        let messages = _processResponseMessages(
          response.data.data,
          response.shop
        );
        setMessages(messages);
        setPageDetails({
          totalPages: response.data.totalPages,
          previousPage: response.data.previousPage,
          nextPage: response.data.nextPage,
          totalItems: response.data.totalItems,
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  /**
   * Load checkouts when page loads
   * or load when the particular state changed
   */
  useEffect(() => {
    const messageType = getMessageType();
    if (messageType) {
      loadOrders([messageType]);
      var array = messageType.split(",");
      const selectedMessageTypes = [];
      for (var i = 0; i < array.length; i++) {
        array[i] = array[i].replace(/^\s*/, "").replace(/\s*$/, "");
        selectedMessageTypes.push(array[i]);
      }
      setMessageTypeSelected(selectedMessageTypes);
    } else {
      loadOrders();
    }

    // eslint-disable-next-line
  }, [page, config]);

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === "" || value == null;
    }
  }
  const messageTypes = {
    post_order_payment: "Order confirmation",
    pre_order: "Abandoned Cart",
    post_order_delivery: "Order delivery",
  };

  function disambiguateLabel(key, value) {
    switch (key) {
      case "messageType":
        return (
          "Selected: " + value.map((val) => `${messageTypes[val]}`).join(", ")
        );
      default:
        return value;
    }
  }
  const [messageTypeSelected, setMessageTypeSelected] = useState(null);
  const [queryValue, setQueryValue] = useState(null);
  const handleMessageTypeSelectedRemove = useCallback(() => {
    loadOrders();
    setMessageTypeSelected(null);
  }, []);
  const handleMessageTypeSelectedChange = useCallback((value) => {
    loadOrders(value);
    setMessageTypeSelected(value);
  }, []);
  const handleQueryValueRemove = useCallback(() => setQueryValue(null), []);
  const handleFiltersQueryChange = useCallback(
    (value) => setQueryValue(value),
    []
  );
  const handleFiltersClearAll = useCallback(() => {
    handleMessageTypeSelectedRemove();
    handleQueryValueRemove();
  }, [handleMessageTypeSelectedRemove, handleQueryValueRemove]);

  const appliedFilters = [];
  if (!isEmpty(messageTypeSelected)) {
    const key = "messageType";
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, messageTypeSelected),
      onRemove: handleMessageTypeSelectedRemove,
    });
  }
  const filters = [
    {
      key: "messageType",
      label: "Filter by message type",
      filter: (
        <ChoiceList
          title="Filter by message type"
          titleHidden
          choices={[
            { label: "Abandoned Cart", value: "pre_order" },
            { label: "Order confirmation", value: "post_order_payment" },
            { label: "Order delivery", value: "post_order_delivery" },
          ]}
          selected={messageTypeSelected || []}
          onChange={handleMessageTypeSelectedChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  return (
    <>
      <Page fullWidth title="Message History" style={{ position: "relative" }}>
        <div class="div2">
          <Filters
            queryValue={queryValue}
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleFiltersQueryChange}
            onQueryClear={handleQueryValueRemove}
            onClearAll={handleFiltersClearAll}
            hideQueryField
          />
        </div>
        <Br height={20} />
        <Card fullWidth>
          {loading ? (
            Array.from({ length: 4 }).map((u, i) => (
              <>
                <div style={{ padding: "20px" }}>
                  <Card title="">
                    <Card.Section>
                      <SkeletonBodyText />
                    </Card.Section>

                    <Card.Section subdued>
                      <SkeletonBodyText />
                    </Card.Section>
                  </Card>
                </div>
              </>
            ))
          ) : messages.length > 0 ? (
            messages.map((message) => (
              <>
                <div style={{ padding: "20px" }}>
                  <Card title="">
                    <Card.Section>
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <div>{message[0]}</div>
                          <div>{message[1]}</div>
                          <div>{message[4]}</div>
                          <div style={{ width: "40%", cursor: "pointer" }}>
                            <Truncate
                              text={message[2].props.children.props.children}
                              maxSize={150}
                            />
                          </div>
                        </div>
                      </>
                    </Card.Section>

                    <Card.Section subdued>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        {message[3]}
                      </div>
                    </Card.Section>
                  </Card>
                </div>
              </>
            ))
          ) : (
            <>
              <div style={{ padding: "20px", textAlign: "center" }}>
                <TextStyle variation="negative">Records not found</TextStyle>
              </div>
            </>
          )}
        </Card>
      </Page>
    </>
  );
}

export default MessageHistory;
